/**
 * TOSS Web Application - Auth Store
 *
 * Zustand store for authentication state management:
 * - Current user information
 * - Authentication status
 * - Login/logout actions
 * - Token management (integrated with API interceptors)
 * - Auto-initialization from stored tokens
 */

import axios from 'axios';
import { create } from 'zustand';
import { User, UserRole } from '@/types';
import { TokenStorage, onAuthLogout, type ApiAudience } from '@/api/interceptors';
import { usePermissionStore } from './permissionStore';
import { authApi } from '@/api/auth.api';
import apiClient from '@/api/client';
import { getMessagingToken } from '@/lib/firebase';
import { queryClient } from '@/app/queryClient';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';
import type { ClientProfileDto } from '@/api/clientPortal/clientCompany.api';

/**
 * "Who am I" endpoint for the given audience. Internal: `/api/lk/users/me` (unchanged).
 * Client: `/api/client/users/me`.
 *
 * The client route lives under `users/`, NOT under `auth/` — profile is a users concern,
 * everything else about the session is an auth concern. Both spec notes agree on this
 * (`client-portal-data-model-api-and-visibility.md` §"Авторизация" and
 * `phase0-part2-client-role-and-schema.md` §1), and it is the one path in the whole
 * audience wiring where getting it wrong breaks login silently: this is the call site
 * `initialize()` uses to restore a session on reload.
 */
function getCurrentUserEndpoint(audience: ApiAudience): string {
  return audience === 'client' ? '/api/client/users/me' : '/api/lk/users/me';
}

// ============================================================================
// Types
// ============================================================================

/**
 * Authentication state interface
 */
interface AuthState {
  /** Current authenticated user */
  user: User | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth state is being initialized (checking stored tokens) */
  isInitializing: boolean;

  /**
   * The session could not be verified because the network was unreachable — NOT because it ended.
   * Set only when `GET /users/me` came back without any response at all. The portal shows a
   * "нет связи" screen with a retry instead of sending the customer to a login form that cannot
   * work offline either; the internal app ignores the flag and behaves exactly as before.
   */
  isOfflineBlocked: boolean;
  /** Whether dynamic permissions have finished loading after login/init */
  isPermissionsLoaded: boolean;
  /** Whether a login/logout operation is in progress */
  isLoading: boolean;
  /** Error message from last auth operation */
  error: string | null;
}

/**
 * Authentication actions interface
 */
interface AuthActions {
  /**
   * Initialize auth state from stored tokens
   * Called on app startup to restore session
   */
  initialize: () => Promise<void>;

  /**
   * Set user and authentication state after successful login
   * @param user - User data from login response
   * @param accessToken - JWT access token
   * @param refreshToken - JWT refresh token
   * @param expiresAt - Token expiration timestamp
   */
  setAuth: (
    user: User,
    accessToken: string,
    refreshToken: string,
    expiresAt: string
  ) => void;

  /**
   * Update current user data
   * @param userData - Partial user data to merge
   */
  updateUser: (userData: Partial<User>) => void;

  /**
   * Clear authentication state (logout)
   * Clears tokens and user data
   */
  logout: () => void;

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: string | null) => void;

  /**
   * Clear error state
   */
  clearError: () => void;
}

/**
 * Combined auth store type
 */
type AuthStore = AuthState & AuthActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isInitializing: true, // Start as true, set to false after initialization
  isPermissionsLoaded: false,
  isLoading: false,
  isOfflineBlocked: false,
  error: null,
};

/**
 * True for a request that never reached the server: no `response`, only a transport failure.
 * A 401/403/500 all carry one, so only genuine offline (or a dead host) lands here.
 */
function isOfflineError(err: unknown): boolean {
  return axios.isAxiosError(err) && !err.response;
}

// ============================================================================
// Store
// ============================================================================

/**
 * Auth store instance
 *
 * Usage:
 * ```tsx
 * const user = useAuthStore((state) => state.user);
 * const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
 * const logout = useAuthStore((state) => state.logout);
 *
 * // Or use selector
 * const { user, isAuthenticated, logout } = useAuthStore();
 * ```
 */
export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  initialize: async () => {
    // Check if tokens exist in storage
    if (!TokenStorage.hasTokens()) {
      set({ isInitializing: false, isAuthenticated: false, user: null });
      return;
    }

    // Check if token is expired
    if (TokenStorage.isTokenExpired()) {
      // Token is expired, but we have a refresh token
      // The API interceptor will handle refresh when we make the /users/me call
      // For now, just mark as potentially authenticated
    }

    try {
      // Fetch current user data. Endpoint depends on the audience the stored session
      // belongs to — see getCurrentUserEndpoint(); the client route is under users/, not auth/.
      const audience = TokenStorage.getAudience();
      const response = await apiClient.get<User>(getCurrentUserEndpoint(audience));

      // Set user data (but keep isInitializing true until permissions load)
      set({
        user: response.data,
        isAuthenticated: true,
        error: null,
      });

      // Client sessions only: the payload this call just fetched IS a ClientProfileDto at
      // runtime (see getCurrentUserEndpoint's own comment — the same "no runtime
      // validation" cast this file already makes onto `User`), including `availableCompanies`
      // since release Р4. Priming TanStack Query's cache with it means `ClientCompanySwitcher`
      // (`useClientProfile()`, mounted in `ClientHeader` for every portal screen) finds a
      // warm cache on its very first render instead of firing a SECOND request at the exact
      // same endpoint this line already just called — the one genuine way to keep the
      // switcher's "no extra request for a one-company account" promise (company-switch
      // contract §6) true for the common case, a page load or reload, rather than only for
      // whichever screen happens to mount `useClientProfile()` first. The narrow remaining
      // gap — a session that just logged in via `setAuth()` and has not gone through this
      // path yet — still costs one request the first time something reads the profile,
      // because `POST /api/client/auth/login`'s own response (`ClientAuthUserDto`) does not
      // carry `availableCompanies` at all.
      if (audience === 'client') {
        queryClient.setQueryData(clientKeys.profile(), response.data as unknown as ClientProfileDto);
      }

      // Load granular permission keys and WAIT for them before finishing initialization
      try {
        await usePermissionStore.getState().fetchMyKeys();
      } catch (err) {
        console.warn('[AuthStore] Failed to load permission keys on init:', err);
        // Continue anyway - will use static fallback
      }

      // Now we can finish initializing — permissions resolved (or fell back to static)
      set({ isInitializing: false, isPermissionsLoaded: true, isOfflineBlocked: false });
    } catch (err) {
      // No response at all means the network failed, not that the session did. Clearing tokens
      // here is what made the installed portal unusable offline: opening it from the home screen
      // with no signal wiped the session and landed the customer on a login form that cannot
      // submit offline either — so the session was gone for real once the network came back.
      if (isOfflineError(err)) {
        set({
          isInitializing: false,
          isPermissionsLoaded: false,
          isOfflineBlocked: true,
          error: null,
        });
        return;
      }

      // Token refresh failed or user fetch failed
      TokenStorage.clearTokens();
      set({
        user: null,
        isAuthenticated: false,
        isInitializing: false,
        isPermissionsLoaded: false,
        isOfflineBlocked: false,
        error: null,
      });
    }
  },

  setAuth: (user, accessToken, refreshToken, expiresAt) => {
    // Store tokens
    TokenStorage.setTokens(accessToken, refreshToken, expiresAt);

    // Record which API space this session belongs to. Derived from user.role rather
    // than threaded through as a setAuth parameter: the backend rejects a Client
    // login at /api/lk/auth/* and /api/mobile/auth/* (and an internal login at
    // /api/client/auth/*) at the entry point itself (phase0-part2 §1, "Р12 спеки
    // отменяется"), so a successful login's user.role is a reliable signal of which
    // audience actually authenticated it — not a guess.
    TokenStorage.setAudience(user.role === UserRole.Client ? 'client' : 'internal');

    // Mark permissions as not yet loaded so guards block until they resolve
    set({
      user,
      isAuthenticated: true,
      isPermissionsLoaded: false,
      isLoading: false,
      error: null,
    });

    // Await granular permission keys so route guards don't flash restricted content.
    // setAuth keeps a void signature — the async work runs in the background but
    // updates isPermissionsLoaded atomically when it completes (or fails).
    usePermissionStore.getState().fetchMyKeys().then(() => {
      set({ isPermissionsLoaded: true });
    }).catch((err) => {
      console.warn('[AuthStore] Failed to load permission keys:', err);
      // Fall back to static matrix — unblock the guards so the user can proceed
      set({ isPermissionsLoaded: true });
    });
  },

  updateUser: (userData) => {
    const currentUser = get().user;
    if (currentUser) {
      set({
        user: { ...currentUser, ...userData },
      });
    }
  },

  logout: () => {
    // Clear tokens
    TokenStorage.clearTokens();

    // Clear dynamic permissions
    usePermissionStore.getState().reset();

    // Reset state
    set({
      user: null,
      isAuthenticated: false,
      isPermissionsLoaded: false,
      isLoading: false,
      error: null,
    });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// ============================================================================
// Auth Event Listener Setup
// ============================================================================

/**
 * Setup listener for auth:logout events from API interceptors
 * This handles automatic logout when token refresh fails
 */
let authLogoutCleanup: (() => void) | null = null;

export function setupAuthEventListener(): void {
  // Cleanup existing listener if any
  if (authLogoutCleanup) {
    authLogoutCleanup();
  }

  // Setup new listener
  authLogoutCleanup = onAuthLogout(() => {
    useAuthStore.getState().logout();
  });
}

/**
 * Cleanup auth event listener
 * Call this when app unmounts or when no longer needed
 */
export function cleanupAuthEventListener(): void {
  if (authLogoutCleanup) {
    authLogoutCleanup();
    authLogoutCleanup = null;
  }
}

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select current user
 */
export const selectUser = (state: AuthStore): User | null => state.user;

/**
 * Select authentication status
 */
export const selectIsAuthenticated = (state: AuthStore): boolean => state.isAuthenticated;

/**
 * Select initialization status
 */
export const selectIsInitializing = (state: AuthStore): boolean => state.isInitializing;

/**
 * Select whether dynamic permissions have finished loading after login/init
 */
export const selectIsAuthPermissionsLoaded = (state: AuthStore): boolean => state.isPermissionsLoaded;

/**
 * Select loading status
 */
export const selectIsLoading = (state: AuthStore): boolean => state.isLoading;

/**
 * Select error message
 */
export const selectError = (state: AuthStore): string | null => state.error;

/**
 * Select user role
 */
export const selectUserRole = (state: AuthStore): UserRole | null => state.user?.role ?? null;

/**
 * Select user ID
 */
export const selectUserId = (state: AuthStore): string | null => state.user?.id ?? null;

/**
 * Check if user has specific role
 */
export const selectHasRole =
  (role: UserRole) =>
  (state: AuthStore): boolean =>
    state.user?.role === role;

/**
 * Check if user has any of the specified roles
 */
export const selectHasAnyRole =
  (roles: UserRole[]) =>
  (state: AuthStore): boolean =>
    state.user ? roles.includes(state.user.role) : false;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get current user with type safety
 */
export function useCurrentUser(): User | null {
  return useAuthStore(selectUser);
}

/**
 * Hook to get authentication status
 */
export function useIsAuthenticated(): boolean {
  return useAuthStore(selectIsAuthenticated);
}

/**
 * Hook to get user role
 */
export function useUserRole(): UserRole | null {
  return useAuthStore(selectUserRole);
}

/**
 * Hook to check if user has specific role
 */
export function useHasRole(role: UserRole): boolean {
  return useAuthStore(selectHasRole(role));
}

/**
 * Hook to check if user has any of specified roles
 */
export function useHasAnyRole(roles: UserRole[]): boolean {
  return useAuthStore(selectHasAnyRole(roles));
}

// ============================================================================
// Logout helper with FCM token cleanup
// ============================================================================

/**
 * Perform full logout with device-token cleanup.
 *
 * Flow:
 * 1. Try to obtain the current FCM token and POST it to
 *    `/api/auth/fcm-token/logout` so only this device's token is removed.
 *    Other devices (e.g. the user's phone) remain subscribed.
 * 2. Clear local auth state via Zustand `logout()`.
 *
 * The FCM cleanup is best-effort: if the browser has `permission=denied`,
 * Firebase isn't configured, or the network call fails, we still proceed
 * with the local logout — the user must always be able to sign out.
 *
 * Skipped entirely for the customer portal: it has no push in Р1 (decision Р12, ред.
 * 2026-08-18), so there is no token to unregister and no endpoint to unregister it at —
 * `getAudienceApiBase()` would resolve to `/api/client/auth/fcm-token/logout`, which
 * `ClientAuthController` does not implement. `authApi.deleteFcmToken()` already
 * short-circuits on the same audience, so the network call is not what this guard
 * prevents: it is `getMessagingToken()`, which registers the Firebase service worker and
 * asks Google for a token no one will ever use — during a logout, of all moments.
 */
export async function logoutWithPushCleanup(): Promise<void> {
  if (TokenStorage.getAudience() === 'client') {
    useAuthStore.getState().logout();
    return;
  }

  try {
    // Only attempt FCM cleanup if permission is already granted. If it's
    // `default` or `denied`, we won't have a token to unregister anyway,
    // and calling `getMessagingToken()` in `default` would pop a permission
    // prompt — which is the opposite of what "Log out" should do.
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      const token = await getMessagingToken();
      if (token) {
        await authApi.deleteFcmToken({ fcmToken: token });
      }
    }
  } catch (error) {
    // Swallow — logout must never fail because of push cleanup.
    console.warn('[AuthStore] FCM token cleanup on logout failed:', error);
  }

  useAuthStore.getState().logout();
}
